// TeacherTimetable.js
import React, { useMemo, useState, useEffect } from "react";
import { Box, Paper, Typography } from "@mui/material";
import apiEndpoints from "../../../apiconfig";

const days = [
  { val: "mon", label: "Monday" },
  { val: "tue", label: "Tuesday" },
  { val: "wed", label: "Wednesday" },
  { val: "thu", label: "Thursday" },
  { val: "fri", label: "Friday" },
  { val: "sat", label: "Saturday" },
];

export default function TeacherTimetable() {

  const [items, setItems] = useState([]);

  // 🔥 ALWAYS Load Dummy Data (NO localStorage)
  useEffect(() => {
    const token = localStorage.getItem("token"); // <--- Get token

    fetch(`${apiEndpoints.timetable}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,   // <--- Required
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
.then((data) => {
  if (data.status === "success") {
    const formatted = data.data.records.map((r) => ({
      id: r.guid,
      classId: r.class_guid,
      className: r.class_name ?? "",
      day: r.day,
      period_no: Number(r.period_no),
      start_time: r.start_time,
      end_time: r.end_time,
      subject: r.subject,
      staff: r.staff_name,
      remarks: r.remarks,
    }));

    setItems(formatted);
  } else {
    console.log("No data");
  }
})

      .catch((err) => console.error(err));
  }, []);
  // Filter teacher timetable
const teacherRows = items; // API already gives only logged staff timetable


if (!teacherRows.length) {
  return (
    <Typography sx={{ p: 3 }}>
      No timetable found.
    </Typography>
  );
}


  // Build period structure
  const periodMap = {};
  teacherRows.forEach((r) => {
    const p = Number(r.period_no);
    if (!periodMap[p]) {
      periodMap[p] = {
        period_no: p,
        start_time: r.start_time,
        end_time: r.end_time,
      };
    }
  });

  const sortedPeriods = Object.values(periodMap).sort(
    (a, b) => a.period_no - b.period_no
  );

  // Build lookup table
  const lookup = {};
  days.forEach((d) => (lookup[d.val] = {}));
  teacherRows.forEach((r) => {
    lookup[r.day][r.period_no] = r;
  });

  return (
    <Box
      sx={{
        minHeight: "auto",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
              `,
          pointerEvents: "none",
        },
        p: 3,
      }}
    >
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: "rgba(255,255,255,0.4)",
            border: "1px solid rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
   My Timetable
</Typography>

        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            p: 1.5,
            background: "transparent",
            border: "1px solid rgba(0,0,0,0.08)",
            overflowX: "auto",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              minWidth: 800,
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: 12,
                    background: "rgba(255, 255, 255, 0.5)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  }}
                >
                  Day / Period
                </th>

                {sortedPeriods.map((p) => (
                  <th
                    key={p.period_no}
                    style={{
                      textAlign: "center",
                      padding: 12,
                      background: "rgba(255, 255, 255, 0.5)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                      minWidth: 140,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>P {p.period_no}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {p.start_time.slice(0,5)} - {p.end_time.slice(0,5)}

                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {days.map((d) => (
                <tr key={d.val}>
                  <td
                    style={{
                      padding: 12,
                      fontWeight: 700,
                      background: "rgba(255, 255, 255, 0.5)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                    }}
                  >
                    {d.label}
                  </td>

                  {sortedPeriods.map((p) => {
                    const entry = lookup[d.val][p.period_no];

                    if (!entry) {
                      return (
                        <td
                          key={p.period_no}
                          style={{
                            padding: 12,
                            border: "1px solid rgba(0,0,0,0.08)",
                            textAlign: "center",
                          }}
                        >
                          —
                        </td>
                      );
                    }

                    return (
                      <td
                        key={p.period_no}
                        style={{
                          padding: 12,
                          border: "2px solid rgba(0,0,0,0.08)",
                          background: "rgba(255, 255, 255, 0.5)",
                          backdropFilter: "blur(16px)",
                          // border: "1px solid rgba(255,255,255,0.5)",
                          boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{entry.subject}</div>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          {entry.className}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Paper>
      </Box>
    </Box>
  );
}
